import { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import type { AnalyticsTrackType } from '@atlassiansox/bxpkit-core';

export const CHECK_EMAIL_PAGE_TRACKING_EVENT_NAMES = {
  CREATE_NEW_ACCOUNT_CLICKED: 'check_email_page_create_new_account_clicked',
  CONTACT_LINK_CLICKED: 'check_email_page_contact_link_clicked',
  RESEND_EMAIL_CLICKED: 'check_email_page_resend_email_clicked',
  RESEND_EMAIL_API_SUCCESS: 'check_email_page_resend_email_api_success',
  RESEND_EMAIL_API_FAILURE: 'check_email_page_resend_email_api_failure'
};

export type GetCheckEmailAnalyticsServiceOptions = {
  readonly baseUrl?: string;
  readonly productKeys?: string[];
  readonly analyticsTrack?: AnalyticsTrackType;
  readonly googleAnalyticsId?: string;
};

export interface CheckEmailPagePropsType {
  readonly layout?: 'default' | 'devops';
  readonly baseUrl?: string;
  readonly productKeys?: string[];
  readonly analyticsTrack?: AnalyticsTrackType;
  readonly hideHeader?: boolean;
}

export type CheckEmailPageLayoutPropsType = {
  readonly email: string;
  readonly shouldEnable: boolean;
  readonly baseUrl?: string;
  readonly hideHeader?: boolean;
  readonly checkEmailAnalyticsService: CheckEmailAnalyticsInterface;
};

export type CheckEmailAnalyticsOptionsType = {
  readonly productKeys?: string[];
  readonly analyticsTrack?: AnalyticsTrackType;
  readonly cartService: CartServiceInterface;
  readonly googleAnalyticsId?: string;
};

export type ResendVerificationEmailRequestType = {
  readonly cloudId?: string;
  readonly cloudName?: string;
  readonly email?: string;
  readonly pagePath?: string;
  readonly requestId?: string;
  readonly primaryProduct?: string;
};

export type HamletClientOptionsType = {
  readonly baseUrl?: string;
};

export type ResendVerificationEmailResponseType = {
  readonly success: boolean;
  readonly status: number;
};

export type HamletPricingRequestType = {
  readonly productKeys: string[];
  readonly licenseType?: string;
  readonly monthsValid?: string | number;
  readonly requestingApplication?: string;
};

export type HamletOrderableItemsType = {
  readonly amount: number;
  readonly description: string;
  readonly edition: unknown;
  readonly editionDescription: string;
  readonly editionId: string;
  readonly editionType: string;
  readonly editionTypeIsDeprecated: boolean;
  readonly enterprise: boolean;
  readonly licenseType: string;
  readonly monthsValid: number;
  readonly orderableItemId: string;
  readonly publiclyAvailable: boolean;
  readonly renewalAmount: number;
  readonly renewalFrequency: string;
  readonly saleType: string;
  readonly sku: unknown;
  readonly starter: boolean;
  readonly unitCount: number;
  readonly unitLabel: string;
};

export type HamletMonthlyType = {
  readonly editionType: string;
  readonly editionTypeIsDeprecated: boolean;
  readonly price: number;
  readonly unitBlockSize: number;
  readonly unitLabel: string;
  readonly unitLimit: number;
  readonly unitStart: number;
  readonly renewalAmount: number;
};

export type HamletProductsType = {
  readonly annual: unknown[];
  readonly billingType: string;
  readonly contactSalesForAdditionalPricing: boolean;
  readonly dataCenter: boolean;
  readonly discountOptOut: boolean;
  readonly lastModified: string;
  readonly marketplaceAddon: boolean;
  readonly parentDescription: string;
  readonly parentKey: string;
  readonly productDescription: string;
  readonly productKey: string;
  readonly productType: string;
  readonly userCountEnforced: boolean;
  readonly monthly: HamletMonthlyType[];
  readonly orderableItems: HamletOrderableItemsType[];
};

export type HamletPricingResponseType = {
  readonly currency: string;
  readonly products: HamletProductsType[];
};

export type HamletProductType = {
  readonly name: string;
} & (HamletOrderableItemsType & HamletMonthlyType & HamletProductsType);

export type HamletClientConstructorType = new ({
  baseUrl
}: HamletClientOptionsType) => HamletClientInterface;

export interface HamletClientInterface {
  hamletPricing({
    productKeys,
    licenseType,
    requestingApplication,
    monthsValid
  }: HamletPricingRequestType): Promise<HamletPricingResponseType>;

  sendEmail(
    resendData: ResendVerificationEmailRequestType
  ): Promise<ResendVerificationEmailResponseType>;

  sendVerificationEmail({
    cloudName,
    email
  }: ResendVerificationEmailRequestType): Promise<ResendVerificationEmailResponseType>;

  resendVerificationEmail({
    cloudId,
    cloudName,
    email,
    pagePath,
    requestId
  }: ResendVerificationEmailRequestType): Promise<ResendVerificationEmailResponseType>;
}

export type CartServiceOptionstype = {
  productKeys?: string[];
  hamletClient: HamletClientInterface;
};

export type CartServiceContructorType = new ({
  productKeys,
  hamletClient
}: CartServiceOptionstype) => CartServiceInterface;

export interface CartServiceInterface {
  getProducts(): Promise<HamletProductType[]>;
  _load(): Promise<any>;
  _parseProducts(pricingData: HamletPricingResponseType): HamletProductType[];
}

export type CheckEmailAnalyticsContructorType = new ({
  cartService,
  analyticsTrack
}: CheckEmailAnalyticsOptionsType) => CheckEmailAnalyticsInterface;

export interface CheckEmailAnalyticsInterface {
  gaId?: string;
  baseUrl?: string;
  analyticsTrack?: AnalyticsTrackType;
  cartService: CartServiceInterface;
  _onloadEvents(): Promise<void>;
  clickResendButton(createAnalyticsEvent: CreateUIAnalyticsEvent): void;
  resendApiNoContent(): void;
  resendApiAlreadyVerified(): void;
  resendApiFailure(): void;
  resendApiSuccess(): void;
  contactLinkAnalytics(createAnalyticsEvent: CreateUIAnalyticsEvent): void;
  navigateBack(
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    createAnalyticsEvent: CreateUIAnalyticsEvent
  ): void;
}
